import React, { createContext, useContext, useEffect, useState } from 'react';
import { extendedModels, extendedCustomers, extendedWarnings } from '../data/mockData';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  // ===== SOURCE DEFINITIONS (Metadata for data sources) =====
  const [sourceDefinitions, setSourceDefinitions] = useState([
    {
      id: 'SRC-001',
      code: 'CORE_BANKING',
      name: 'Core Banking System',
      type: 'database',
      description: 'Hệ thống ngân hàng lõi',
      status: 'active',
      templateFiles: []
    },
    {
      id: 'SRC-002',
      code: 'CIC_DATA',
      name: 'Dữ liệu CIC',
      type: 'external',
      description: 'Thông tin tín dụng từ CIC',
      status: 'active',
      templateFiles: [
        { name: 'credit_score.csv', path: '/templates/credit_score.csv', type: 'csv', description: 'Template điểm tín dụng CIC' }
      ]
    },
    {
      id: 'SRC-003',
      code: 'CUSTOMER_EXCEL',
      name: 'Báo cáo khách hàng',
      type: 'manual',
      description: 'File Excel báo cáo khách hàng',
      status: 'active',
      templateFiles: [
        { name: 'customer_data.csv', path: '/templates/customer_data.csv', type: 'csv', description: 'Template thông tin khách hàng' },
        { name: 'ews_data_template.xlsx', path: '/templates/ews_data_template.xlsx', type: 'excel', description: 'Template Excel tổng hợp (4 sheets)', size: '80KB' }
      ]
    },
    {
      id: 'SRC-004',
      code: 'CREDIT_DB',
      name: 'Hệ thống tín dụng',
      type: 'database',
      description: 'Database quản lý tín dụng',
      status: 'active',
      templateFiles: [
        { name: 'loan_data.csv', path: '/templates/loan_data.csv', type: 'csv', description: 'Template dữ liệu khoản vay' }
      ]
    },
    {
      id: 'SRC-005',
      code: 'RISK_SYSTEM',
      name: 'Hệ thống quản lý rủi ro',
      type: 'database',
      description: 'Hệ thống đánh giá và quản lý rủi ro',
      status: 'active',
      templateFiles: []
    },
    {
      id: 'SRC-006',
      code: 'TRANSACTION_LOG',
      name: 'Dữ liệu giao dịch',
      type: 'database',
      description: 'Log giao dịch của khách hàng',
      status: 'active',
      templateFiles: [
        { name: 'transaction_data.csv', path: '/templates/transaction_data.csv', type: 'csv', description: 'Template dữ liệu giao dịch' }
      ]
    },
    {
      id: 'SRC-007',
      code: 'COMPLIANCE_DATA',
      name: 'Dữ liệu tuân thủ',
      type: 'external',
      description: 'Dữ liệu báo cáo tuân thủ và kiểm toán',
      status: 'active',
      templateFiles: [
        { name: 'compliance_report.csv', path: '/templates/compliance_report.csv', type: 'csv', description: 'Template báo cáo tuân thủ' }
      ]
    }
  ]);

  // ===== DATA SOURCES =====
  const [dataSources, setDataSources] = useState([
    {
      id: 'DS-001',
      name: 'CORE_BANKING',
      type: 'Database',
      dbType: 'Oracle',
      host: 'oracle.bank.vn',
      port: 1521,
      database: 'CORE_DB',
      username: 'core_user',
      status: 'connected',
      lastSync: '2025-12-15 14:30:00',
      structure: {
        tables: [
          {
            name: 'CUSTOMERS',
            fields: [
              { name: 'CUSTOMER_ID', type: 'NUMBER', nullable: false },
              { name: 'FULL_NAME', type: 'VARCHAR2', nullable: false },
              { name: 'BALANCE', type: 'NUMBER', nullable: true },
              { name: 'CREDIT_SCORE', type: 'NUMBER', nullable: true }
            ]
          },
          {
            name: 'LOANS',
            fields: [
              { name: 'LOAN_ID', type: 'NUMBER', nullable: false },
              { name: 'CUSTOMER_ID', type: 'NUMBER', nullable: false },
              { name: 'AMOUNT', type: 'NUMBER', nullable: false },
              { name: 'OVERDUE_DAYS', type: 'NUMBER', nullable: true }
            ]
          }
        ]
      }
    },
    {
      id: 'DS-002',
      name: 'CIC_DATA',
      type: 'CSV',
      files: ['cic_scores.csv', 'credit_history.csv'],
      status: 'connected',
      lastSync: '2025-12-15 10:00:00',
      structure: {
        files: [
          {
            name: 'cic_scores.csv',
            fields: [
              { name: 'customer_id', type: 'STRING', nullable: false },
              { name: 'cic_score', type: 'NUMBER', nullable: false },
              { name: 'rating', type: 'STRING', nullable: true }
            ]
          }
        ]
      }
    }
  ]);

  // ===== INDICATORS =====
  const [indicators, setIndicators] = useState([
    {
      id: 'IND-FIN-001',
      code: 'NPL_RATIO',
      name: 'Tỷ lệ nợ quá hạn',
      description: 'Tính tỷ lệ nợ quá hạn trên tổng dư nợ',
      category: 'Tài chính',
      targetObject: 'Khách hàng cá nhân',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng ngày',
      status: 'active',
      complexity: 'medium',
      variables: [
        {
          id: 'var1',
          customName: 'Tổng nợ quá hạn',
          dataSource: 'CORE_BANKING',
          table: 'LOANS',
          field: 'AMOUNT',
          dataType: 'NUMBER',
          filter: 'OVERDUE_DAYS > 90'
        },
        {
          id: 'var2',
          customName: 'Tổng dư nợ',
          dataSource: 'CORE_BANKING',
          table: 'LOANS',
          field: 'AMOUNT',
          dataType: 'NUMBER'
        }
      ],
      formulas: ['(var1 / var2) * 100'],
      unit: '%',
      thresholds: [
        { operator: '>=', value: 10, severity: 'high', action: 'Cảnh báo cao' },
        { operator: '>=', value: 5, severity: 'medium', action: 'Cảnh báo trung bình' },
        { operator: '<', value: 5, severity: 'low', action: 'Theo dõi' }
      ],
      createdDate: '2025-12-01',
      updatedDate: '2025-12-15'
    },
    {
      id: 'IND-PAY-001',
      code: 'LATE_PAYMENT',
      name: 'Số lần thanh toán trễ',
      description: 'Đếm số lần thanh toán trễ trong 12 tháng',
      category: 'Thanh toán',
      targetObject: 'Khách hàng cá nhân',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng tháng',
      status: 'active',
      complexity: 'low',
      variables: [
        {
          id: 'var1',
          customName: 'Số lần trễ',
          dataSource: 'CORE_BANKING',
          table: 'LOANS',
          field: 'LOAN_ID',
          dataType: 'NUMBER',
          filter: 'OVERDUE_DAYS > 0'
        }
      ],
      formulas: ['COUNT(var1)'],
      unit: 'Lần',
      thresholds: [
        { operator: '>=', value: 3, severity: 'high', action: 'Cảnh báo cao' },
        { operator: '>=', value: 1, severity: 'medium', action: 'Theo dõi' }
      ],
      createdDate: '2025-12-05',
      updatedDate: '2025-12-10'
    },
    {
      id: 'IND-BEH-001',
      code: 'CIC_SCORE',
      name: 'Điểm tín dụng CIC',
      description: 'Điểm tín dụng từ Trung tâm Thông tin Tín dụng',
      category: 'Bên ngoài',
      targetObject: 'Khách hàng cá nhân',
      dataSource: 'CIC_DATA',
      collectType: 'Tự động',
      collectFrequency: 'Hàng tháng',
      status: 'active',
      complexity: 'low',
      variables: [
        {
          id: 'var1',
          customName: 'Điểm CIC',
          dataSource: 'CIC_DATA',
          file: 'cic_scores.csv',
          field: 'cic_score',
          dataType: 'NUMBER'
        }
      ],
      formulas: ['var1'],
      unit: 'Điểm',
      thresholds: [
        { operator: '<', value: 500, severity: 'high', action: 'Từ chối tín dụng' },
        { operator: '<', value: 650, severity: 'medium', action: 'Xem xét kỹ' },
        { operator: '>=', value: 650, severity: 'low', action: 'Chấp nhận' }
      ],
      createdDate: '2025-12-03',
      updatedDate: '2025-12-12'
    },
    {
      id: 'IND-FIN-002',
      code: 'DTI_RATIO',
      name: 'Tỷ lệ thu nhập/nợ',
      description: 'Debt-to-Income ratio',
      category: 'Tài chính',
      targetObject: 'Khách hàng cá nhân',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng tháng',
      status: 'active',
      complexity: 'medium',
      variables: [
        {
          id: 'var1',
          customName: 'Tổng nợ hàng tháng',
          dataSource: 'CORE_BANKING',
          table: 'LOANS',
          field: 'AMOUNT',
          dataType: 'NUMBER'
        },
        {
          id: 'var2',
          customName: 'Thu nhập hàng tháng',
          dataSource: 'CORE_BANKING',
          table: 'CUSTOMERS',
          field: 'BALANCE',
          dataType: 'NUMBER'
        }
      ],
      formulas: ['(var1 / var2) * 100'],
      unit: '%',
      thresholds: [
        { operator: '>=', value: 50, severity: 'high', action: 'Cảnh báo cao' },
        { operator: '>=', value: 30, severity: 'medium', action: 'Theo dõi' }
      ],
      createdDate: '2025-12-08',
      updatedDate: '2025-12-14'
    },
    {
      id: 'IND-BEH-002',
      code: 'ACCOUNT_COUNT',
      name: 'Số tài khoản đồng sở hữu',
      description: 'Số lượng tài khoản vay/tín dụng khách hàng đang sử dụng đồng thời',
      category: 'Hành vi',
      targetObject: 'Khách hàng cá nhân',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng tuần',
      status: 'active',
      complexity: 'low',
      variables: [
        {
          id: 'var1',
          customName: 'Số tài khoản đang hoạt động',
          dataSource: 'CORE_BANKING',
          table: 'LOANS',
          field: 'LOAN_ID',
          dataType: 'NUMBER',
          filter: 'STATUS = "ACTIVE"'
        }
      ],
      formulas: ['COUNT(var1)'],
      unit: 'Tài khoản',
      thresholds: [
        { operator: '>=', value: 5, severity: 'high', action: 'Cảnh báo cao - Quá tải tài chính' },
        { operator: '>=', value: 3, severity: 'medium', action: 'Theo dõi - Nhiều khoản vay' },
        { operator: '<', value: 3, severity: 'low', action: 'Bình thường' }
      ],
      createdDate: '2025-12-16',
      updatedDate: '2025-12-16'
    },
    {
      id: 'IND-EXT-001',
      code: 'COLLATERAL_CHANGE',
      name: 'Biến động giá trị tài sản thế chấp',
      description: 'Tỷ lệ thay đổi giá trị tài sản thế chấp so với thời điểm đánh giá ban đầu',
      category: 'Bên ngoài',
      targetObject: 'Khách hàng doanh nghiệp',
      dataSource: 'CORE_BANKING',
      collectType: 'Thủ công',
      collectFrequency: 'Hàng quý',
      status: 'active',
      complexity: 'high',
      variables: [
        {
          id: 'var1',
          customName: 'Giá trị hiện tại',
          dataSource: 'CORE_BANKING',
          table: 'LOANS',
          field: 'AMOUNT',
          dataType: 'NUMBER'
        },
        {
          id: 'var2',
          customName: 'Giá trị ban đầu',
          dataSource: 'CORE_BANKING',
          table: 'LOANS',
          field: 'AMOUNT',
          dataType: 'NUMBER'
        }
      ],
      formulas: ['((var1 - var2) / var2) * 100'],
      unit: '%',
      thresholds: [
        { operator: '<=', value: -30, severity: 'high', action: 'Cảnh báo cao - Giảm giá mạnh' },
        { operator: '<=', value: -15, severity: 'medium', action: 'Theo dõi - Giảm giá nhẹ' },
        { operator: '>', value: -15, severity: 'low', action: 'Ổn định hoặc tăng giá' }
      ],
      createdDate: '2025-12-17',
      updatedDate: '2025-12-17'
    },
    {
      id: 'IND-PAY-002',
      code: 'ONTIME_RATIO',
      name: 'Tỷ lệ thanh toán đúng hạn',
      description: 'Tỷ lệ phần trăm các kỳ thanh toán được thực hiện đúng hoặc trước hạn',
      category: 'Thanh toán',
      targetObject: 'Tất cả',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng ngày',
      status: 'active',
      complexity: 'low',
      variables: [
        {
          id: 'var1',
          customName: 'Số lần thanh toán đúng hạn',
          dataSource: 'CORE_BANKING',
          table: 'LOANS',
          field: 'LOAN_ID',
          dataType: 'NUMBER',
          filter: 'OVERDUE_DAYS = 0'
        },
        {
          id: 'var2',
          customName: 'Tổng số lần thanh toán',
          dataSource: 'CORE_BANKING',
          table: 'LOANS',
          field: 'LOAN_ID',
          dataType: 'NUMBER'
        }
      ],
      formulas: ['(var1 / var2) * 100'],
      unit: '%',
      thresholds: [
        { operator: '<', value: 50, severity: 'high', action: 'Cảnh báo cao - Thường xuyên trễ' },
        { operator: '<', value: 80, severity: 'medium', action: 'Theo dõi - Đôi khi trễ' },
        { operator: '>=', value: 80, severity: 'low', action: 'Tốt - Thanh toán đều đặn' }
      ],
      createdDate: '2025-12-18',
      updatedDate: '2025-12-18'
    },
    // ===== TÀI CHÍNH (thêm 8 indicators) =====
    {
      id: 'IND-FIN-003',
      code: 'LIQUIDITY_RATIO',
      name: 'Tỷ lệ thanh khoản',
      description: 'Khả năng thanh toán nợ ngắn hạn',
      category: 'Tài chính',
      targetObject: 'Khách hàng doanh nghiệp',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng quý',
      status: 'active',
      complexity: 'medium',
      variables: [
        { id: 'var1', customName: 'Tài sản ngắn hạn', dataSource: 'CORE_BANKING', table: 'CUSTOMERS', field: 'BALANCE', dataType: 'NUMBER' },
        { id: 'var2', customName: 'Nợ ngắn hạn', dataSource: 'CORE_BANKING', table: 'LOANS', field: 'AMOUNT', dataType: 'NUMBER' }
      ],
      formulas: ['var1 / var2'],
      unit: 'Lần',
      thresholds: [
        { operator: '<', value: 1, severity: 'high', action: 'Thanh khoản kém' },
        { operator: '<', value: 1.5, severity: 'medium', action: 'Thanh khoản thấp' },
        { operator: '>=', value: 1.5, severity: 'low', action: 'Thanh khoản tốt' }
      ],
      createdDate: '2026-01-10',
      updatedDate: '2026-01-10'
    },
    {
      id: 'IND-FIN-004',
      code: 'PROFIT_MARGIN',
      name: 'Tỷ suất lợi nhuận',
      description: 'Hiệu quả sinh lời trên doanh thu',
      category: 'Tài chính',
      targetObject: 'Khách hàng doanh nghiệp',
      dataSource: 'CORE_BANKING',
      collectType: 'Thủ công',
      collectFrequency: 'Hàng quý',
      status: 'active',
      complexity: 'medium',
      variables: [
        { id: 'var1', customName: 'Lợi nhuận ròng', dataSource: 'CORE_BANKING', table: 'CUSTOMERS', field: 'BALANCE', dataType: 'NUMBER' },
        { id: 'var2', customName: 'Doanh thu', dataSource: 'CORE_BANKING', table: 'CUSTOMERS', field: 'BALANCE', dataType: 'NUMBER' }
      ],
      formulas: ['(var1 / var2) * 100'],
      unit: '%',
      thresholds: [
        { operator: '<', value: 5, severity: 'high', action: 'Lợi nhuận thấp' },
        { operator: '<', value: 15, severity: 'medium', action: 'Lợi nhuận trung bình' },
        { operator: '>=', value: 15, severity: 'low', action: 'Lợi nhuận tốt' }
      ],
      createdDate: '2026-01-12',
      updatedDate: '2026-01-12'
    },
    {
      id: 'IND-FIN-005',
      code: 'ASSET_TURNOVER',
      name: 'Vòng quay tài sản',
      description: 'Hiệu quả sử dụng tài sản',
      category: 'Tài chính',
      targetObject: 'Khách hàng doanh nghiệp',
      dataSource: 'CORE_BANKING',
      collectType: 'Thủ công',
      collectFrequency: 'Hàng năm',
      status: 'active',
      complexity: 'medium',
      variables: [
        { id: 'var1', customName: 'Doanh thu thuần', dataSource: 'CORE_BANKING', table: 'CUSTOMERS', field: 'BALANCE', dataType: 'NUMBER' },
        { id: 'var2', customName: 'Tổng tài sản', dataSource: 'CORE_BANKING', table: 'CUSTOMERS', field: 'BALANCE', dataType: 'NUMBER' }
      ],
      formulas: ['var1 / var2'],
      unit: 'Lần',
      thresholds: [
        { operator: '<', value: 0.5, severity: 'high', action: 'Hiệu quả thấp' },
        { operator: '<', value: 1, severity: 'medium', action: 'Hiệu quả trung bình' },
        { operator: '>=', value: 1, severity: 'low', action: 'Hiệu quả tốt' }
      ],
      createdDate: '2026-01-15',
      updatedDate: '2026-01-15'
    },
    {
      id: 'IND-FIN-006',
      code: 'EQUITY_RATIO',
      name: 'Tỷ lệ vốn chủ sở hữu',
      description: 'Tỷ lệ vốn tự có trên tổng tài sản',
      category: 'Tài chính',
      targetObject: 'Khách hàng doanh nghiệp',
      dataSource: 'CORE_BANKING',
      collectType: 'Thủ công',
      collectFrequency: 'Hàng quý',
      status: 'active',
      complexity: 'low',
      variables: [
        { id: 'var1', customName: 'Vốn chủ sở hữu', dataSource: 'CORE_BANKING', table: 'CUSTOMERS', field: 'BALANCE', dataType: 'NUMBER' },
        { id: 'var2', customName: 'Tổng tài sản', dataSource: 'CORE_BANKING', table: 'CUSTOMERS', field: 'BALANCE', dataType: 'NUMBER' }
      ],
      formulas: ['(var1 / var2) * 100'],
      unit: '%',
      thresholds: [
        { operator: '<', value: 20, severity: 'high', action: 'Vốn quá thấp' },
        { operator: '<', value: 40, severity: 'medium', action: 'Vốn thấp' },
        { operator: '>=', value: 40, severity: 'low', action: 'Vốn ổn định' }
      ],
      createdDate: '2026-01-18',
      updatedDate: '2026-01-18'
    },
    {
      id: 'IND-FIN-007',
      code: 'CASH_FLOW',
      name: 'Dòng tiền hoạt động',
      description: 'Dòng tiền từ hoạt động kinh doanh',
      category: 'Tài chính',
      targetObject: 'Khách hàng doanh nghiệp',
      dataSource: 'CORE_BANKING',
      collectType: 'Thủ công',
      collectFrequency: 'Hàng quý',
      status: 'active',
      complexity: 'high',
      variables: [
        { id: 'var1', customName: 'Dòng tiền thuần', dataSource: 'CORE_BANKING', table: 'CUSTOMERS', field: 'BALANCE', dataType: 'NUMBER' }
      ],
      formulas: ['var1'],
      unit: 'VND',
      thresholds: [
        { operator: '<', value: 0, severity: 'high', action: 'Dòng tiền âm' },
        { operator: '<', value: 100000000, severity: 'medium', action: 'Dòng tiền thấp' },
        { operator: '>=', value: 100000000, severity: 'low', action: 'Dòng tiền tốt' }
      ],
      createdDate: '2026-01-20',
      updatedDate: '2026-01-20'
    },
    {
      id: 'IND-FIN-008',
      code: 'WORKING_CAPITAL',
      name: 'Vốn lưu động',
      description: 'Vốn hoạt động ngắn hạn',
      category: 'Tài chính',
      targetObject: 'Khách hàng doanh nghiệp',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng tháng',
      status: 'active',
      complexity: 'medium',
      variables: [
        { id: 'var1', customName: 'Tài sản ngắn hạn', dataSource: 'CORE_BANKING', table: 'CUSTOMERS', field: 'BALANCE', dataType: 'NUMBER' },
        { id: 'var2', customName: 'Nợ ngắn hạn', dataSource: 'CORE_BANKING', table: 'LOANS', field: 'AMOUNT', dataType: 'NUMBER' }
      ],
      formulas: ['var1 - var2'],
      unit: 'VND',
      thresholds: [
        { operator: '<', value: 0, severity: 'high', action: 'Thiếu vốn lưu động' },
        { operator: '<', value: 500000000, severity: 'medium', action: 'Vốn lưu động thấp' },
        { operator: '>=', value: 500000000, severity: 'low', action: 'Vốn lưu động đủ' }
      ],
      createdDate: '2026-01-22',
      updatedDate: '2026-01-22'
    },
    {
      id: 'IND-FIN-009',
      code: 'INTEREST_COVERAGE',
      name: 'Khả năng thanh toán lãi vay',
      description: 'Tỷ lệ EBIT trên chi phí lãi vay',
      category: 'Tài chính',
      targetObject: 'Khách hàng doanh nghiệp',
      dataSource: 'CORE_BANKING',
      collectType: 'Thủ công',
      collectFrequency: 'Hàng quý',
      status: 'active',
      complexity: 'high',
      variables: [
        { id: 'var1', customName: 'EBIT', dataSource: 'CORE_BANKING', table: 'CUSTOMERS', field: 'BALANCE', dataType: 'NUMBER' },
        { id: 'var2', customName: 'Chi phí lãi vay', dataSource: 'CORE_BANKING', table: 'LOANS', field: 'AMOUNT', dataType: 'NUMBER' }
      ],
      formulas: ['var1 / var2'],
      unit: 'Lần',
      thresholds: [
        { operator: '<', value: 1.5, severity: 'high', action: 'Không đủ khả năng' },
        { operator: '<', value: 3, severity: 'medium', action: 'Khả năng thấp' },
        { operator: '>=', value: 3, severity: 'low', action: 'Khả năng tốt' }
      ],
      createdDate: '2026-01-25',
      updatedDate: '2026-01-25'
    },
    {
      id: 'IND-FIN-010',
      code: 'REVENUE_GROWTH',
      name: 'Tăng trưởng doanh thu',
      description: 'Tỷ lệ tăng trưởng doanh thu so với kỳ trước',
      category: 'Tài chính',
      targetObject: 'Khách hàng doanh nghiệp',
      dataSource: 'CORE_BANKING',
      collectType: 'Thủ công',
      collectFrequency: 'Hàng quý',
      status: 'active',
      complexity: 'medium',
      variables: [
        { id: 'var1', customName: 'Doanh thu kỳ này', dataSource: 'CORE_BANKING', table: 'CUSTOMERS', field: 'BALANCE', dataType: 'NUMBER' },
        { id: 'var2', customName: 'Doanh thu kỳ trước', dataSource: 'CORE_BANKING', table: 'CUSTOMERS', field: 'BALANCE', dataType: 'NUMBER' }
      ],
      formulas: ['((var1 - var2) / var2) * 100'],
      unit: '%',
      thresholds: [
        { operator: '<', value: 0, severity: 'high', action: 'Doanh thu giảm' },
        { operator: '<', value: 10, severity: 'medium', action: 'Tăng trưởng thấp' },
        { operator: '>=', value: 10, severity: 'low', action: 'Tăng trưởng tốt' }
      ],
      createdDate: '2026-01-28',
      updatedDate: '2026-01-28'
    },
    // ===== THANH TOÁN (thêm 8 indicators) =====
    {
      id: 'IND-PAY-003',
      code: 'PAYMENT_FREQUENCY',
      name: 'Tần suất thanh toán',
      description: 'Số lần thanh toán trong tháng',
      category: 'Thanh toán',
      targetObject: 'Tất cả',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng tháng',
      status: 'active',
      complexity: 'low',
      variables: [
        { id: 'var1', customName: 'Số lần thanh toán', dataSource: 'CORE_BANKING', table: 'LOANS', field: 'LOAN_ID', dataType: 'NUMBER' }
      ],
      formulas: ['COUNT(var1)'],
      unit: 'Lần',
      thresholds: [
        { operator: '<', value: 1, severity: 'high', action: 'Không thanh toán' },
        { operator: '<', value: 2, severity: 'medium', action: 'Ít thanh toán' },
        { operator: '>=', value: 2, severity: 'low', action: 'Thanh toán đều' }
      ],
      createdDate: '2026-02-01',
      updatedDate: '2026-02-01'
    },
    {
      id: 'IND-PAY-004',
      code: 'OVERDUE_AMOUNT',
      name: 'Số tiền quá hạn',
      description: 'Tổng số tiền thanh toán quá hạn',
      category: 'Thanh toán',
      targetObject: 'Tất cả',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng ngày',
      status: 'active',
      complexity: 'low',
      variables: [
        { id: 'var1', customName: 'Tiền quá hạn', dataSource: 'CORE_BANKING', table: 'LOANS', field: 'AMOUNT', dataType: 'NUMBER', filter: 'OVERDUE_DAYS > 0' }
      ],
      formulas: ['SUM(var1)'],
      unit: 'VND',
      thresholds: [
        { operator: '>', value: 50000000, severity: 'high', action: 'Quá hạn cao' },
        { operator: '>', value: 10000000, severity: 'medium', action: 'Quá hạn trung bình' },
        { operator: '<=', value: 10000000, severity: 'low', action: 'Quá hạn thấp' }
      ],
      createdDate: '2026-02-03',
      updatedDate: '2026-02-03'
    },
    {
      id: 'IND-PAY-005',
      code: 'EARLY_PAYMENT',
      name: 'Thanh toán trước hạn',
      description: 'Số lần thanh toán trước hạn',
      category: 'Thanh toán',
      targetObject: 'Tất cả',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng tháng',
      status: 'active',
      complexity: 'low',
      variables: [
        { id: 'var1', customName: 'Số lần trả trước', dataSource: 'CORE_BANKING', table: 'LOANS', field: 'LOAN_ID', dataType: 'NUMBER', filter: 'OVERDUE_DAYS < 0' }
      ],
      formulas: ['COUNT(var1)'],
      unit: 'Lần',
      thresholds: [
        { operator: '>=', value: 5, severity: 'low', action: 'Thanh toán tốt' },
        { operator: '>=', value: 2, severity: 'medium', action: 'Thanh toán ổn' },
        { operator: '<', value: 2, severity: 'high', action: 'Ít trả trước' }
      ],
      createdDate: '2026-02-05',
      updatedDate: '2026-02-05'
    },
    {
      id: 'IND-PAY-006',
      code: 'PAYMENT_METHOD',
      name: 'Phương thức thanh toán',
      description: 'Đa dạng phương thức thanh toán',
      category: 'Thanh toán',
      targetObject: 'Tất cả',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng tháng',
      status: 'active',
      complexity: 'low',
      variables: [
        { id: 'var1', customName: 'Số phương thức', dataSource: 'CORE_BANKING', table: 'LOANS', field: 'LOAN_ID', dataType: 'NUMBER' }
      ],
      formulas: ['COUNT(DISTINCT var1)'],
      unit: 'Loại',
      thresholds: [
        { operator: '>=', value: 3, severity: 'low', action: 'Đa dạng' },
        { operator: '>=', value: 2, severity: 'medium', action: 'Trung bình' },
        { operator: '<', value: 2, severity: 'high', action: 'Đơn điệu' }
      ],
      createdDate: '2026-02-07',
      updatedDate: '2026-02-07'
    },
    {
      id: 'IND-PAY-007',
      code: 'PAYMENT_HISTORY',
      name: 'Lịch sử thanh toán 12 tháng',
      description: 'Điểm đánh giá lịch sử thanh toán',
      category: 'Thanh toán',
      targetObject: 'Tất cả',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng tháng',
      status: 'active',
      complexity: 'medium',
      variables: [
        { id: 'var1', customName: 'Tháng trả đúng', dataSource: 'CORE_BANKING', table: 'LOANS', field: 'LOAN_ID', dataType: 'NUMBER', filter: 'OVERDUE_DAYS = 0' }
      ],
      formulas: ['(COUNT(var1) / 12) * 100'],
      unit: '%',
      thresholds: [
        { operator: '<', value: 60, severity: 'high', action: 'Lịch sử kém' },
        { operator: '<', value: 90, severity: 'medium', action: 'Lịch sử trung bình' },
        { operator: '>=', value: 90, severity: 'low', action: 'Lịch sử tốt' }
      ],
      createdDate: '2026-02-10',
      updatedDate: '2026-02-10'
    },
    {
      id: 'IND-PAY-008',
      code: 'PARTIAL_PAYMENT',
      name: 'Thanh toán từng phần',
      description: 'Tỷ lệ thanh toán không đầy đủ',
      category: 'Thanh toán',
      targetObject: 'Tất cả',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng tháng',
      status: 'active',
      complexity: 'medium',
      variables: [
        { id: 'var1', customName: 'Số lần trả thiếu', dataSource: 'CORE_BANKING', table: 'LOANS', field: 'LOAN_ID', dataType: 'NUMBER' },
        { id: 'var2', customName: 'Tổng số lần trả', dataSource: 'CORE_BANKING', table: 'LOANS', field: 'LOAN_ID', dataType: 'NUMBER' }
      ],
      formulas: ['(var1 / var2) * 100'],
      unit: '%',
      thresholds: [
        { operator: '>', value: 30, severity: 'high', action: 'Thường xuyên thiếu' },
        { operator: '>', value: 10, severity: 'medium', action: 'Đôi khi thiếu' },
        { operator: '<=', value: 10, severity: 'low', action: 'Thanh toán đủ' }
      ],
      createdDate: '2026-02-12',
      updatedDate: '2026-02-12'
    },
    {
      id: 'IND-PAY-009',
      code: 'AUTOPAY_RATE',
      name: 'Tỷ lệ thanh toán tự động',
      description: 'Tỷ lệ sử dụng thanh toán tự động',
      category: 'Thanh toán',
      targetObject: 'Tất cả',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng tháng',
      status: 'active',
      complexity: 'low',
      variables: [
        { id: 'var1', customName: 'Auto payment', dataSource: 'CORE_BANKING', table: 'LOANS', field: 'LOAN_ID', dataType: 'NUMBER' },
        { id: 'var2', customName: 'Total payment', dataSource: 'CORE_BANKING', table: 'LOANS', field: 'LOAN_ID', dataType: 'NUMBER' }
      ],
      formulas: ['(var1 / var2) * 100'],
      unit: '%',
      thresholds: [
        { operator: '>=', value: 80, severity: 'low', action: 'Tự động cao' },
        { operator: '>=', value: 50, severity: 'medium', action: 'Tự động trung bình' },
        { operator: '<', value: 50, severity: 'high', action: 'Tự động thấp' }
      ],
      createdDate: '2026-02-15',
      updatedDate: '2026-02-15'
    },
    {
      id: 'IND-PAY-010',
      code: 'PAYMENT_TREND',
      name: 'Xu hướng thanh toán',
      description: 'Xu hướng cải thiện/xấu đi thanh toán',
      category: 'Thanh toán',
      targetObject: 'Tất cả',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng quý',
      status: 'active',
      complexity: 'high',
      variables: [
        { id: 'var1', customName: 'Điểm quý này', dataSource: 'CORE_BANKING', table: 'LOANS', field: 'LOAN_ID', dataType: 'NUMBER' },
        { id: 'var2', customName: 'Điểm quý trước', dataSource: 'CORE_BANKING', table: 'LOANS', field: 'LOAN_ID', dataType: 'NUMBER' }
      ],
      formulas: ['((var1 - var2) / var2) * 100'],
      unit: '%',
      thresholds: [
        { operator: '<', value: -20, severity: 'high', action: 'Xấu đi nhiều' },
        { operator: '<', value: 0, severity: 'medium', action: 'Xấu đi ít' },
        { operator: '>=', value: 0, severity: 'low', action: 'Cải thiện' }
      ],
      createdDate: '2026-02-18',
      updatedDate: '2026-02-18'
    },
    // ===== HÀNH VI (thêm 8 indicators) =====
    {
      id: 'IND-BEH-003',
      code: 'TRANSACTION_FREQ',
      name: 'Tần suất giao dịch',
      description: 'Số lần giao dịch trong tháng',
      category: 'Hành vi',
      targetObject: 'Tất cả',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng tháng',
      status: 'active',
      complexity: 'low',
      variables: [
        { id: 'var1', customName: 'Số giao dịch', dataSource: 'CORE_BANKING', table: 'LOANS', field: 'LOAN_ID', dataType: 'NUMBER' }
      ],
      formulas: ['COUNT(var1)'],
      unit: 'Lần',
      thresholds: [
        { operator: '<', value: 5, severity: 'high', action: 'Hoạt động thấp' },
        { operator: '<', value: 20, severity: 'medium', action: 'Hoạt động trung bình' },
        { operator: '>=', value: 20, severity: 'low', action: 'Hoạt động tốt' }
      ],
      createdDate: '2026-02-20',
      updatedDate: '2026-02-20'
    },
    {
      id: 'IND-BEH-004',
      code: 'BALANCE_VOLATILITY',
      name: 'Biến động số dư',
      description: 'Độ dao động số dư tài khoản',
      category: 'Hành vi',
      targetObject: 'Tất cả',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng tháng',
      status: 'active',
      complexity: 'medium',
      variables: [
        { id: 'var1', customName: 'Độ lệch chuẩn', dataSource: 'CORE_BANKING', table: 'CUSTOMERS', field: 'BALANCE', dataType: 'NUMBER' },
        { id: 'var2', customName: 'Trung bình', dataSource: 'CORE_BANKING', table: 'CUSTOMERS', field: 'BALANCE', dataType: 'NUMBER' }
      ],
      formulas: ['(var1 / var2) * 100'],
      unit: '%',
      thresholds: [
        { operator: '>', value: 50, severity: 'high', action: 'Biến động lớn' },
        { operator: '>', value: 30, severity: 'medium', action: 'Biến động trung bình' },
        { operator: '<=', value: 30, severity: 'low', action: 'Ổn định' }
      ],
      createdDate: '2026-02-22',
      updatedDate: '2026-02-22'
    },
    {
      id: 'IND-BEH-005',
      code: 'WITHDRAWAL_PATTERN',
      name: 'Mẫu hình rút tiền',
      description: 'Tần suất và số lượng rút tiền',
      category: 'Hành vi',
      targetObject: 'Khách hàng cá nhân',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng tuần',
      status: 'active',
      complexity: 'medium',
      variables: [
        { id: 'var1', customName: 'Số lần rút', dataSource: 'CORE_BANKING', table: 'LOANS', field: 'LOAN_ID', dataType: 'NUMBER' },
        { id: 'var2', customName: 'Tổng tiền rút', dataSource: 'CORE_BANKING', table: 'LOANS', field: 'AMOUNT', dataType: 'NUMBER' }
      ],
      formulas: ['var2 / var1'],
      unit: 'VND',
      thresholds: [
        { operator: '>', value: 50000000, severity: 'high', action: 'Rút lớn' },
        { operator: '>', value: 20000000, severity: 'medium', action: 'Rút trung bình' },
        { operator: '<=', value: 20000000, severity: 'low', action: 'Rút nhỏ' }
      ],
      createdDate: '2026-02-25',
      updatedDate: '2026-02-25'
    },
    {
      id: 'IND-BEH-006',
      code: 'LOGIN_FREQUENCY',
      name: 'Tần suất đăng nhập',
      description: 'Số lần đăng nhập app/internet banking',
      category: 'Hành vi',
      targetObject: 'Tất cả',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng tháng',
      status: 'active',
      complexity: 'low',
      variables: [
        { id: 'var1', customName: 'Số lần login', dataSource: 'CORE_BANKING', table: 'CUSTOMERS', field: 'BALANCE', dataType: 'NUMBER' }
      ],
      formulas: ['COUNT(var1)'],
      unit: 'Lần',
      thresholds: [
        { operator: '>=', value: 20, severity: 'low', action: 'Tích cực' },
        { operator: '>=', value: 10, severity: 'medium', action: 'Trung bình' },
        { operator: '<', value: 10, severity: 'high', action: 'Ít tương tác' }
      ],
      createdDate: '2026-02-28',
      updatedDate: '2026-02-28'
    },
    {
      id: 'IND-BEH-007',
      code: 'PRODUCT_USAGE',
      name: 'Sử dụng sản phẩm',
      description: 'Số lượng sản phẩm/dịch vụ đang sử dụng',
      category: 'Hành vi',
      targetObject: 'Tất cả',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng tháng',
      status: 'active',
      complexity: 'low',
      variables: [
        { id: 'var1', customName: 'Số sản phẩm', dataSource: 'CORE_BANKING', table: 'LOANS', field: 'LOAN_ID', dataType: 'NUMBER' }
      ],
      formulas: ['COUNT(DISTINCT var1)'],
      unit: 'Sản phẩm',
      thresholds: [
        { operator: '>=', value: 5, severity: 'low', action: 'Cross-sell tốt' },
        { operator: '>=', value: 3, severity: 'medium', action: 'Cross-sell trung bình' },
        { operator: '<', value: 3, severity: 'high', action: 'Ít sản phẩm' }
      ],
      createdDate: '2026-03-01',
      updatedDate: '2026-03-01'
    },
    {
      id: 'IND-BEH-008',
      code: 'COMPLAINT_COUNT',
      name: 'Số lượng khiếu nại',
      description: 'Số lần khiếu nại trong 12 tháng',
      category: 'Hành vi',
      targetObject: 'Tất cả',
      dataSource: 'CORE_BANKING',
      collectType: 'Thủ công',
      collectFrequency: 'Hàng tháng',
      status: 'active',
      complexity: 'low',
      variables: [
        { id: 'var1', customName: 'Số khiếu nại', dataSource: 'CORE_BANKING', table: 'CUSTOMERS', field: 'BALANCE', dataType: 'NUMBER' }
      ],
      formulas: ['COUNT(var1)'],
      unit: 'Lần',
      thresholds: [
        { operator: '>=', value: 5, severity: 'high', action: 'Khiếu nại nhiều' },
        { operator: '>=', value: 2, severity: 'medium', action: 'Khiếu nại vừa' },
        { operator: '<', value: 2, severity: 'low', action: 'Ít khiếu nại' }
      ],
      createdDate: '2026-03-05',
      updatedDate: '2026-03-05'
    },
    {
      id: 'IND-BEH-009',
      code: 'LOYALTY_YEARS',
      name: 'Thời gian trung thành',
      description: 'Số năm là khách hàng của ngân hàng',
      category: 'Hành vi',
      targetObject: 'Tất cả',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng năm',
      status: 'active',
      complexity: 'low',
      variables: [
        { id: 'var1', customName: 'Năm tham gia', dataSource: 'CORE_BANKING', table: 'CUSTOMERS', field: 'BALANCE', dataType: 'NUMBER' }
      ],
      formulas: ['YEAR(NOW()) - var1'],
      unit: 'Năm',
      thresholds: [
        { operator: '>=', value: 10, severity: 'low', action: 'Khách hàng lâu năm' },
        { operator: '>=', value: 3, severity: 'medium', action: 'Khách hàng ổn định' },
        { operator: '<', value: 3, severity: 'high', action: 'Khách hàng mới' }
      ],
      createdDate: '2026-03-08',
      updatedDate: '2026-03-08'
    },
    {
      id: 'IND-BEH-010',
      code: 'CHANNEL_PREFERENCE',
      name: 'Ưu tiên kênh giao dịch',
      description: 'Tỷ lệ sử dụng kênh digital',
      category: 'Hành vi',
      targetObject: 'Tất cả',
      dataSource: 'CORE_BANKING',
      collectType: 'Tự động',
      collectFrequency: 'Hàng tháng',
      status: 'active',
      complexity: 'medium',
      variables: [
        { id: 'var1', customName: 'GD digital', dataSource: 'CORE_BANKING', table: 'LOANS', field: 'LOAN_ID', dataType: 'NUMBER' },
        { id: 'var2', customName: 'Tổng GD', dataSource: 'CORE_BANKING', table: 'LOANS', field: 'LOAN_ID', dataType: 'NUMBER' }
      ],
      formulas: ['(var1 / var2) * 100'],
      unit: '%',
      thresholds: [
        { operator: '>=', value: 80, severity: 'low', action: 'Digital savvy' },
        { operator: '>=', value: 50, severity: 'medium', action: 'Hybrid user' },
        { operator: '<', value: 50, severity: 'high', action: 'Traditional user' }
      ],
      createdDate: '2026-03-10',
      updatedDate: '2026-03-10'
    },
    // ===== BÊN NGOÀI (thêm 9 indicators) =====
    {
      id: 'IND-EXT-002',
      code: 'MARKET_SHARE',
      name: 'Thị phần ngành',
      description: 'Vị trí thị phần trong ngành',
      category: 'Bên ngoài',
      targetObject: 'Khách hàng doanh nghiệp',
      dataSource: 'EXTERNAL_DATA',
      collectType: 'Thủ công',
      collectFrequency: 'Hàng năm',
      status: 'active',
      complexity: 'high',
      variables: [
        { id: 'var1', customName: 'Doanh thu DN', dataSource: 'CORE_BANKING', table: 'CUSTOMERS', field: 'BALANCE', dataType: 'NUMBER' },
        { id: 'var2', customName: 'Tổng thị trường', dataSource: 'EXTERNAL_DATA', file: 'market_data.csv', field: 'total_market', dataType: 'NUMBER' }
      ],
      formulas: ['(var1 / var2) * 100'],
      unit: '%',
      thresholds: [
        { operator: '>=', value: 20, severity: 'low', action: 'Thị phần lớn' },
        { operator: '>=', value: 5, severity: 'medium', action: 'Thị phần trung bình' },
        { operator: '<', value: 5, severity: 'high', action: 'Thị phần nhỏ' }
      ],
      createdDate: '2026-03-12',
      updatedDate: '2026-03-12'
    },
    {
      id: 'IND-EXT-003',
      code: 'INDUSTRY_GROWTH',
      name: 'Tăng trưởng ngành',
      description: 'Tốc độ tăng trưởng của ngành',
      category: 'Bên ngoài',
      targetObject: 'Khách hàng doanh nghiệp',
      dataSource: 'EXTERNAL_DATA',
      collectType: 'Thủ công',
      collectFrequency: 'Hàng quý',
      status: 'active',
      complexity: 'medium',
      variables: [
        { id: 'var1', customName: 'GDP ngành', dataSource: 'EXTERNAL_DATA', file: 'industry_data.csv', field: 'industry_gdp', dataType: 'NUMBER' }
      ],
      formulas: ['var1'],
      unit: '%',
      thresholds: [
        { operator: '<', value: 0, severity: 'high', action: 'Ngành suy thoái' },
        { operator: '<', value: 5, severity: 'medium', action: 'Ngành tăng chậm' },
        { operator: '>=', value: 5, severity: 'low', action: 'Ngành tăng tốt' }
      ],
      createdDate: '2026-03-15',
      updatedDate: '2026-03-15'
    },
    {
      id: 'IND-EXT-004',
      code: 'COMPETITOR_COUNT',
      name: 'Số lượng đối thủ',
      description: 'Mức độ cạnh tranh trong ngành',
      category: 'Bên ngoài',
      targetObject: 'Khách hàng doanh nghiệp',
      dataSource: 'EXTERNAL_DATA',
      collectType: 'Thủ công',
      collectFrequency: 'Hàng năm',
      status: 'active',
      complexity: 'medium',
      variables: [
        { id: 'var1', customName: 'Số đối thủ', dataSource: 'EXTERNAL_DATA', file: 'market_data.csv', field: 'competitors', dataType: 'NUMBER' }
      ],
      formulas: ['var1'],
      unit: 'Đơn vị',
      thresholds: [
        { operator: '>', value: 100, severity: 'high', action: 'Cạnh tranh khốc liệt' },
        { operator: '>', value: 50, severity: 'medium', action: 'Cạnh tranh cao' },
        { operator: '<=', value: 50, severity: 'low', action: 'Cạnh tranh vừa' }
      ],
      createdDate: '2026-03-18',
      updatedDate: '2026-03-18'
    },
    {
      id: 'IND-EXT-005',
      code: 'REGULATORY_RISK',
      name: 'Rủi ro quy định',
      description: 'Mức độ thay đổi chính sách',
      category: 'Bên ngoài',
      targetObject: 'Khách hàng doanh nghiệp',
      dataSource: 'EXTERNAL_DATA',
      collectType: 'Thủ công',
      collectFrequency: 'Hàng quý',
      status: 'active',
      complexity: 'high',
      variables: [
        { id: 'var1', customName: 'Điểm rủi ro', dataSource: 'EXTERNAL_DATA', file: 'regulatory_data.csv', field: 'risk_score', dataType: 'NUMBER' }
      ],
      formulas: ['var1'],
      unit: 'Điểm',
      thresholds: [
        { operator: '>', value: 7, severity: 'high', action: 'Rủi ro cao' },
        { operator: '>', value: 4, severity: 'medium', action: 'Rủi ro trung bình' },
        { operator: '<=', value: 4, severity: 'low', action: 'Rủi ro thấp' }
      ],
      createdDate: '2026-03-20',
      updatedDate: '2026-03-20'
    },
    {
      id: 'IND-EXT-006',
      code: 'ECONOMIC_INDICATOR',
      name: 'Chỉ số kinh tế vĩ mô',
      description: 'GDP, lạm phát, lãi suất',
      category: 'Bên ngoài',
      targetObject: 'Tất cả',
      dataSource: 'EXTERNAL_DATA',
      collectType: 'Tự động',
      collectFrequency: 'Hàng quý',
      status: 'active',
      complexity: 'medium',
      variables: [
        { id: 'var1', customName: 'GDP growth', dataSource: 'EXTERNAL_DATA', file: 'macro_data.csv', field: 'gdp_growth', dataType: 'NUMBER' }
      ],
      formulas: ['var1'],
      unit: '%',
      thresholds: [
        { operator: '<', value: 3, severity: 'high', action: 'Kinh tế yếu' },
        { operator: '<', value: 6, severity: 'medium', action: 'Kinh tế ổn' },
        { operator: '>=', value: 6, severity: 'low', action: 'Kinh tế tốt' }
      ],
      createdDate: '2026-03-22',
      updatedDate: '2026-03-22'
    },
    {
      id: 'IND-EXT-007',
      code: 'EXCHANGE_RATE',
      name: 'Tỷ giá hối đoái',
      description: 'Rủi ro tỷ giá cho DN xuất nhập khẩu',
      category: 'Bên ngoài',
      targetObject: 'Khách hàng doanh nghiệp',
      dataSource: 'EXTERNAL_DATA',
      collectType: 'Tự động',
      collectFrequency: 'Hàng ngày',
      status: 'active',
      complexity: 'medium',
      variables: [
        { id: 'var1', customName: 'Biến động tỷ giá', dataSource: 'EXTERNAL_DATA', file: 'forex_data.csv', field: 'volatility', dataType: 'NUMBER' }
      ],
      formulas: ['var1'],
      unit: '%',
      thresholds: [
        { operator: '>', value: 5, severity: 'high', action: 'Biến động mạnh' },
        { operator: '>', value: 2, severity: 'medium', action: 'Biến động vừa' },
        { operator: '<=', value: 2, severity: 'low', action: 'Biến động nhẹ' }
      ],
      createdDate: '2026-03-25',
      updatedDate: '2026-03-25'
    },
    {
      id: 'IND-EXT-008',
      code: 'SOCIAL_MEDIA',
      name: 'Uy tín mạng xã hội',
      description: 'Sentiment và engagement trên mạng xã hội',
      category: 'Bên ngoài',
      targetObject: 'Khách hàng doanh nghiệp',
      dataSource: 'EXTERNAL_DATA',
      collectType: 'Tự động',
      collectFrequency: 'Hàng tuần',
      status: 'active',
      complexity: 'high',
      variables: [
        { id: 'var1', customName: 'Sentiment score', dataSource: 'EXTERNAL_DATA', file: 'social_data.csv', field: 'sentiment', dataType: 'NUMBER' }
      ],
      formulas: ['var1'],
      unit: 'Điểm',
      thresholds: [
        { operator: '<', value: -0.3, severity: 'high', action: 'Uy tín kém' },
        { operator: '<', value: 0.3, severity: 'medium', action: 'Uy tín trung bình' },
        { operator: '>=', value: 0.3, severity: 'low', action: 'Uy tín tốt' }
      ],
      createdDate: '2026-03-28',
      updatedDate: '2026-03-28'
    },
    {
      id: 'IND-EXT-009',
      code: 'SUPPLIER_RISK',
      name: 'Rủi ro nhà cung cấp',
      description: 'Đánh giá độ tin cậy chuỗi cung ứng',
      category: 'Bên ngoài',
      targetObject: 'Khách hàng doanh nghiệp',
      dataSource: 'EXTERNAL_DATA',
      collectType: 'Thủ công',
      collectFrequency: 'Hàng quý',
      status: 'active',
      complexity: 'high',
      variables: [
        { id: 'var1', customName: 'Số NCC có vấn đề', dataSource: 'EXTERNAL_DATA', file: 'supplier_data.csv', field: 'risky_suppliers', dataType: 'NUMBER' },
        { id: 'var2', customName: 'Tổng NCC', dataSource: 'EXTERNAL_DATA', file: 'supplier_data.csv', field: 'total_suppliers', dataType: 'NUMBER' }
      ],
      formulas: ['(var1 / var2) * 100'],
      unit: '%',
      thresholds: [
        { operator: '>', value: 30, severity: 'high', action: 'Chuỗi cung ứng yếu' },
        { operator: '>', value: 10, severity: 'medium', action: 'Chuỗi cung ứng vừa' },
        { operator: '<=', value: 10, severity: 'low', action: 'Chuỗi cung ứng tốt' }
      ],
      createdDate: '2026-04-01',
      updatedDate: '2026-04-01'
    },
    {
      id: 'IND-EXT-010',
      code: 'LEGAL_CASE',
      name: 'Tranh chấp pháp lý',
      description: 'Số vụ kiện tụng đang xử lý',
      category: 'Bên ngoài',
      targetObject: 'Khách hàng doanh nghiệp',
      dataSource: 'EXTERNAL_DATA',
      collectType: 'Thủ công',
      collectFrequency: 'Hàng quý',
      status: 'active',
      complexity: 'medium',
      variables: [
        { id: 'var1', customName: 'Số vụ kiện', dataSource: 'EXTERNAL_DATA', file: 'legal_data.csv', field: 'case_count', dataType: 'NUMBER' }
      ],
      formulas: ['var1'],
      unit: 'Vụ',
      thresholds: [
        { operator: '>', value: 5, severity: 'high', action: 'Nhiều tranh chấp' },
        { operator: '>', value: 2, severity: 'medium', action: 'Có tranh chấp' },
        { operator: '<=', value: 2, severity: 'low', action: 'Ít tranh chấp' }
      ],
      createdDate: '2026-04-05',
      updatedDate: '2026-04-05'
    }
  ]);

  // ===== MODELS =====
  const [models, setModels] = useState(extendedModels);

  // ===== CUSTOMERS =====
  const [customers, setCustomers] = useState(extendedCustomers);

  // ===== CUSTOMER GROUPS =====
  const [customerGroups, setCustomerGroups] = useState([
    {
      id: 'VIP',
      code: 'VIP',
      name: 'VIP',
      category: 'Phân khúc',
      description: 'Khách hàng VIP có giá trị cao',
      criteria: 'Dư nợ > 50 tỷ hoặc Tài sản gửi > 100 tỷ',
      riskWeight: 1.0,
      customerIds: [],
      status: 'active',
      createdDate: '01/01/2026'
    },
    {
      id: 'SME',
      code: 'SME',
      name: 'Doanh nghiệp vừa và nhỏ',
      category: 'Phân khúc',
      description: 'Doanh nghiệp vừa và nhỏ',
      criteria: 'Doanh thu < 100 tỷ/năm',
      riskWeight: 1.2,
      customerIds: ['KH003'],
      status: 'active',
      createdDate: '01/01/2026'
    },
    {
      id: 'CORP',
      code: 'CORP',
      name: 'Doanh nghiệp lớn',
      category: 'Phân khúc',
      description: 'Doanh nghiệp lớn và tập đoàn',
      criteria: 'Doanh thu >= 100 tỷ/năm',
      riskWeight: 1.1,
      customerIds: ['KH001'],
      status: 'active',
      createdDate: '01/01/2026'
    },
    {
      id: 'RETAIL',
      code: 'RETAIL',
      name: 'Khách hàng cá nhân',
      category: 'Phân khúc',
      description: 'Khách hàng cá nhân bán lẻ',
      criteria: 'Cá nhân, không thuộc VIP',
      riskWeight: 1.3,
      customerIds: ['KH002', 'KH004', 'KH005'],
      status: 'active',
      createdDate: '01/01/2026'
    },
    {
      id: 'HIGH-RISK',
      code: 'HIGH-RISK',
      name: 'Rủi ro cao',
      category: 'Rủi ro',
      description: 'Khách hàng có mức độ rủi ro cao',
      criteria: 'Nợ quá hạn > 30 ngày hoặc Điểm rủi ro < 600',
      riskWeight: 2.5,
      customerIds: ['KH001', 'KH006'],
      status: 'active',
      createdDate: '10/01/2026'
    },
    {
      id: 'NPL',
      code: 'NPL',
      name: 'Nợ xấu',
      category: 'Rủi ro',
      description: 'Nhóm nợ xấu cần xử lý',
      criteria: 'Nợ quá hạn > 90 ngày',
      riskWeight: 5.0,
      customerIds: [],
      status: 'active',
      createdDate: '15/01/2026'
    },
    {
      id: 'STARTUP',
      code: 'STARTUP',
      name: 'Startup',
      category: 'Phân khúc',
      description: 'Doanh nghiệp khởi nghiệp',
      criteria: 'Doanh nghiệp < 3 năm tuổi',
      riskWeight: 1.8,
      customerIds: [],
      status: 'active',
      createdDate: '20/02/2026'
    }
  ]);

  const getRiskRecommendations = (riskLevel) => {
    if (riskLevel === 'high') {
      return [
        'Liên hệ khách hàng ngay để xác minh tình hình tài chính',
        'Yêu cầu bổ sung tài liệu chứng minh dòng tiền',
        'Tăng tần suất giám sát lên hàng tuần'
      ];
    }

    if (riskLevel === 'medium') {
      return [
        'Theo dõi sát trong 2-4 tuần tới',
        'Gửi nhắc nhở về nghĩa vụ thanh toán',
        'Đánh giá lại vào kỳ chấm tiếp theo'
      ];
    }

    return [
      'Tiếp tục theo dõi định kỳ',
      'Duy trì mức cảnh báo hiện tại'
    ];
  };

  const scoreToIndicatorRisk = (scoreValue) => {
    if (scoreValue >= 3) return 'high';
    if (scoreValue >= 2) return 'medium';
    if (scoreValue >= 1) return 'low';
    return 'none';
  };

  const buildInitWarningIndicators = (warning, sourceModels = models, sourceIndicators = indicators) => {
    const model = sourceModels.find(m => m.id === warning.modelId);
    const modelIndicators = Array.isArray(model?.indicators) ? model.indicators : [];
    const indicatorDefs = modelIndicators.length > 0
      ? modelIndicators
      : (warning.indicatorId ? [{ id: warning.indicatorId, weight: 1, frequency: 'Hàng tháng' }] : []);

    if (indicatorDefs.length === 0) {
      return [];
    }

    const weightTotal = indicatorDefs.reduce((sum, item) => sum + (Number(item.weight) || 0), 0) || indicatorDefs.length;
    const warningRatio = (Number(warning.maxScore) || 0) > 0 ? (Number(warning.score) || 0) / Number(warning.maxScore) : 0;
    const checkedDate = warning.lastCheckedDate || warning.createdDate || new Date().toISOString().slice(0, 16).replace('T', ' ');

    return indicatorDefs.map((item, idx) => {
      const indicatorMeta = sourceIndicators.find(ind => ind.id === item.id);
      const normalizedWeight = (Number(item.weight) || 1) / weightTotal;
      const baseline = warningRatio * 4;
      const score = Math.min(4, Math.max(0, baseline + (idx * 0.1) - 0.1));
      const roundedScore = Number(score.toFixed(2));

      return {
        id: item.id || warning.indicatorId || `IND-AUTO-${idx + 1}`,
        name: indicatorMeta?.name || warning.indicatorName || `Chỉ tiêu ${idx + 1}`,
        category: indicatorMeta?.category || 'Khác',
        weight: Number(normalizedWeight.toFixed(4)),
        frequency: item.frequency || indicatorMeta?.collectFrequency || 'Hàng tháng',
        actualValue: Number((warningRatio * 100 * normalizedWeight).toFixed(2)),
        unit: indicatorMeta?.unit || 'điểm',
        score: roundedScore,
        maxScore: 4,
        riskLevel: scoreToIndicatorRisk(roundedScore),
        checkedDate
      };
    });
  };

  const normalizeInitWarning = (warning, sourceModels = models, sourceIndicators = indicators) => {
    const hasIndicatorData = Array.isArray(warning.indicators) && warning.indicators.length > 0;
    const hasHistoryData = Array.isArray(warning.history) && warning.history.length > 0;
    const hasRecommendations = Array.isArray(warning.recommendations) && warning.recommendations.length > 0;

    if (hasIndicatorData && hasHistoryData && hasRecommendations) {
      return warning;
    }

    const riskLevel = warning.riskLevel || 'low';
    const now = warning.lastCheckedDate || warning.createdDate || new Date().toISOString().slice(0, 16).replace('T', ' ');

    return {
      ...warning,
      indicators: hasIndicatorData ? warning.indicators : buildInitWarningIndicators(warning, sourceModels, sourceIndicators),
      recommendations: hasRecommendations ? warning.recommendations : getRiskRecommendations(riskLevel),
      history: hasHistoryData ? warning.history : [
        {
          id: 1,
          date: now,
          score: warning.score,
          riskLevel,
          status: warning.status || 'new',
          action: 'Khởi tạo dữ liệu cảnh báo',
          user: warning.createdBy || 'System Init',
          notes: 'Tự động khởi tạo dữ liệu chi tiết cho bản ghi seed.'
        }
      ]
    };
  };

  // ===== WARNINGS =====
  const [warnings, setWarnings] = useState(() => extendedWarnings.map(warning => normalizeInitWarning(warning)));

  // ===== DATA SOURCES CRUD =====
  const addDataSource = (dataSource) => {
    const newId = `DS-${String(dataSources.length + 1).padStart(3, '0')}`;
    setDataSources([...dataSources, { ...dataSource, id: newId }]);
  };

  const updateDataSource = (id, updates) => {
    setDataSources(dataSources.map(ds => ds.id === id ? { ...ds, ...updates } : ds));
  };

  const deleteDataSource = (id) => {
    setDataSources(dataSources.filter(ds => ds.id !== id));
  };

  // ===== INDICATORS CRUD =====
  const addIndicator = (indicator) => {
    const categoryPrefix = {
      'Tài chính': 'FIN',
      'Thanh toán': 'PAY',
      'Hành vi': 'BEH',
      'Bên ngoài': 'EXT',
      'Khác': 'OTH'
    };
    const prefix = categoryPrefix[indicator.category] || 'GEN';
    const count = indicators.filter(ind => ind.category === indicator.category).length + 1;
    const newId = `IND-${prefix}-${String(count).padStart(3, '0')}`;
    
    setIndicators([...indicators, { 
      ...indicator, 
      id: newId,
      createdDate: new Date().toISOString().split('T')[0],
      updatedDate: new Date().toISOString().split('T')[0]
    }]);
  };

  const updateIndicator = (id, updates) => {
    setIndicators(indicators.map(ind => 
      ind.id === id 
        ? { ...ind, ...updates, updatedDate: new Date().toISOString().split('T')[0] } 
        : ind
    ));
  };

  const deleteIndicator = (id) => {
    setIndicators(indicators.filter(ind => ind.id !== id));
  };

  const toggleIndicatorStatus = (id) => {
    setIndicators(indicators.map(ind =>
      ind.id === id
        ? { ...ind, status: ind.status === 'active' ? 'inactive' : 'active' }
        : ind
    ));
  };

  // ===== MODELS CRUD =====
  const addModel = (model) => {
    const targetPrefix = {
      'Khách hàng cá nhân': 'RET',
      'Khách hàng doanh nghiệp': 'COR',
      'Các ĐCTC': 'DCT',
      'Tất cả': 'ALL'
    };
    const prefix = targetPrefix[model.targetObject] || 'GEN';
    const count = models.filter(m => m.targetObject === model.targetObject).length + 1;
    const newId = `MDL-${prefix}-${String(count).padStart(3, '0')}`;
    const code = `${prefix}_EWS`;
    
    setModels([...models, {
      ...model,
      id: newId,
      code,
      status: 'active',
      createdDate: new Date().toISOString().split('T')[0]
    }]);
  };

  const updateModel = (id, updates) => {
    setModels(models.map(m => m.id === id ? { ...m, ...updates } : m));
  };

  const deleteModel = (id) => {
    setModels(models.filter(m => m.id !== id));
  };

  const toggleModelStatus = (id) => {
    setModels(models.map(m =>
      m.id === id
        ? { ...m, status: m.status === 'active' ? 'inactive' : 'active' }
        : m
    ));
  };

  // Helper: Get indicator details by ID
  const getIndicatorById = (id) => {
    return indicators.find(ind => ind.id === id);
  };

  // Helper: Get active indicators for Models
  const getActiveIndicators = () => {
    return indicators.filter(ind => ind.status === 'active');
  };

  // ===== CUSTOMERS CRUD =====
  const addCustomer = (customer) => {
    const count = customers.length + 1;
    const newId = `C${String(count).padStart(5, '0')}`;
    setCustomers([...customers, { ...customer, id: newId, code: newId, createdDate: new Date().toISOString().slice(0, 16).replace('T', ' ') }]);
  };

  const updateCustomer = (id, updates) => {
    setCustomers(customers.map(c => c.id === id ? { ...c, ...updates } : c));
  };

  const deleteCustomer = (id) => {
    setCustomers(customers.filter(c => c.id !== id));
  };

  const getCustomerById = (id) => {
    return customers.find(c => c.id === id);
  };

  // ===== CUSTOMER GROUPS CRUD =====
  const addCustomerGroup = (group) => {
    const newGroup = {
      ...group,
      id: group.code,
      createdDate: new Date().toLocaleDateString('vi-VN')
    };
    setCustomerGroups([...customerGroups, newGroup]);
  };

  const updateCustomerGroup = (id, updates) => {
    setCustomerGroups(customerGroups.map(g => g.id === id ? { ...g, ...updates } : g));
  };

  const deleteCustomerGroup = (id) => {
    setCustomerGroups(customerGroups.filter(g => g.id !== id));
  };

  const getCustomerGroupById = (id) => {
    return customerGroups.find(g => g.id === id);
  };

  // ===== WARNINGS CRUD =====
  const addWarning = (warning) => {
    const count = warnings.length + 1;
    const newId = `ALT-${String(count).padStart(3, '0')}`;
    const newWarning = { ...warning, id: newId, code: newId };
    setWarnings([...warnings, newWarning]);
    
    // Auto-filter: Check if warning matches any active rules
    setTimeout(() => applyAutoFilterToWarning(newWarning), 100);
  };
  
  // Apply auto-filter rules to a warning
  const applyAutoFilterToWarning = (warning) => {
    // Get customer info
    const customer = customers.find(c => c.id === warning.customerId);
    if (!customer) return;
    
    // Check if case already exists for this warning
    const existingCase = cases.find(c => c.warningId === warning.id);
    if (existingCase) return; // Case already created
    
    // Check if warning matches any active rule
    const activeRules = caseFilterRules.filter(r => r.isActive);
    
    for (const rule of activeRules) {
      let matchCount = 0;
      let totalConditions = 0;
      
      // Check risk level
      if (rule.riskLevels && rule.riskLevels.length > 0) {
        totalConditions++;
        if (rule.riskLevels.includes(warning.riskLevel)) {
          matchCount++;
        }
      }
      
      // Check customer type
      if (rule.customerTypes && rule.customerTypes.length > 0) {
        totalConditions++;
        if (rule.customerTypes.includes(warning.customerType || customer?.type)) {
          matchCount++;
        }
      }
      
      // Check customer group
      if (rule.customerGroups && rule.customerGroups.length > 0) {
        totalConditions++;
        if (rule.customerGroups.includes(customer.groupId)) {
          matchCount++;
        }
      }
      
      // Determine if rule matches
      let matched = false;
      if (rule.matchCondition === 'any') {
        matched = matchCount > 0;
      } else {
        matched = totalConditions > 0 && matchCount === totalConditions;
      }
      
      // If matched, create case automatically
      if (matched) {
        const autoCase = {
          id: `CASE-${Date.now()}-AUTO`,
          warningId: warning.id,
          warningCode: warning.code,
          warningLevel: warning.riskLevel,
          warningScore: warning.score,
          alertType: warning.alertType,
          customerId: customer.id,
          customerName: customer.name,
          customerType: warning.customerType || customer.type,
          riskLevel: warning.riskLevel,
          currentWorkflow: 'cbs',
          currentStepId: 'cbs_4',
          status: 'in_progress',
          isManual: false, // Auto-created case
          isHidden: false,
          ruleId: rule.id, // Track which rule created this case
          ruleName: rule.name,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          history: [
            {
              stepId: 'cbs_1',
              action: 'auto_created',
              timestamp: new Date().toISOString(),
              user: 'System',
              notes: `Case tự động tạo từ rule: ${rule.name}`,
              status: 'completed'
            }
          ],
          data: {
            warningData: {
              score: warning.score,
              maxScore: warning.maxScore,
              thresholds: warning.thresholds,
              details: warning.details,
              model: warning.modelName
            }
          }
        };
        
        // Add case
        setCases(prev => [...prev, autoCase]);
        
        // Update warning status
        setWarnings(prev => prev.map(w => 
          w.id === warning.id ? { ...w, status: 'in_progress' } : w
        ));
        
        // Only create one case per warning (first matching rule)
        break;
      }
    }
  };


  const updateWarning = (id, updates) => {
    setWarnings(warnings.map(w => w.id === id ? { ...w, ...updates } : w));
  };

  const deleteWarning = (id) => {
    setWarnings(warnings.filter(w => w.id !== id));
  };

  const getWarningById = (id) => {
    return warnings.find(w => w.id === id);
  };

  const addWarningHistory = (warningId, historyEntry) => {
    setWarnings(warnings.map(w => {
      if (w.id === warningId) {
        const currentHistory = Array.isArray(w.history) ? w.history : [];
        return {
          ...w,
          history: [{ id: currentHistory.length + 1, ...historyEntry }, ...currentHistory]
        };
      }
      return w;
    }));
  };

  // Helper: Calculate risk score for a customer based on a model
  const calculateRiskScore = (customer, model, previousScore = null) => {
    // Random score with slight variation from previous if exists
    let baseScore;
    if (previousScore !== null) {
      // Variation within ±10 points
      const variation = (Math.random() - 0.5) * 20;
      baseScore = Math.max(0, Math.min(100, previousScore + variation));
    } else {
      baseScore = Math.random() * 100;
    }

    // Calculate indicator scores
    const indicatorScores = model.indicators.map(modelInd => {
      const indicator = getIndicatorById(modelInd.id);
      if (!indicator) return null;

      // Random actual value and score
      const actualValue = Math.random() * 20; // Mock value
      const score = Math.random() * 4; // Score out of 4
      
      let riskLevel = 'none';
      if (score >= 3) riskLevel = 'high';
      else if (score >= 2) riskLevel = 'medium';
      else if (score >= 1) riskLevel = 'low';

      return {
        id: indicator.id,
        name: indicator.name,
        category: indicator.category,
        weight: modelInd.weight,
        frequency: modelInd.frequency,
        actualValue: parseFloat(actualValue.toFixed(2)),
        unit: indicator.unit || 'N/A',
        score: parseFloat(score.toFixed(2)),
        maxScore: 4,
        riskLevel,
        checkedDate: new Date().toISOString().slice(0, 16).replace('T', ' ')
      };
    }).filter(Boolean);

    // Calculate overall score
    const totalScore = indicatorScores.reduce((sum, ind) => sum + (ind.score * ind.weight * 100 / 4), 0);
    let finalScore = Math.round(totalScore);

    // Make rerun effects visible: if score stays identical, force a small ±1 change.
    if (previousScore !== null && finalScore === Math.round(previousScore)) {
      const delta = Math.random() < 0.5 ? -1 : 1;
      finalScore = Math.max(0, Math.min(100, finalScore + delta));
    }

    // Determine risk level based on model thresholds
    let riskLevel = 'none';
    const scorePercent = finalScore / 100 * 4; // Convert to 0-4 scale
    if (scorePercent >= model.thresholds.high.min) riskLevel = 'high';
    else if (scorePercent >= model.thresholds.medium.min) riskLevel = 'medium';
    else if (scorePercent >= model.thresholds.low.min) riskLevel = 'low';

    return {
      score: finalScore,
      riskLevel,
      indicators: indicatorScores
    };
  };

  // Run warning check for a single customer
  const runWarningForCustomer = (customerId, modelId, username = 'System Auto', warningId = null) => {
    const targetWarning = warningId ? warnings.find(w => w.id === warningId) : null;
    const customer =
      getCustomerById(customerId) ||
      customers.find(c => c.code === customerId) ||
      (targetWarning ? customers.find(c => c.id === targetWarning.customerId || c.code === targetWarning.customerId) : null);
    const model = models.find(m => m.id === modelId);
    
    if (!customer || !model) {
      return null;
    }

    // Get previous warning if exists. Prioritize explicit warning id from UI rerun action.
    const existingWarning = targetWarning || warnings.find(
      w => (w.customerId === customerId || w.customerId === customer?.code || w.customerId === customer?.id) &&
        w.modelId === modelId &&
        w.status !== 'resolved'
    );
    const previousScore = existingWarning ? existingWarning.score : null;

    // Calculate new score
    const { score, riskLevel, indicators: indicatorScores } = calculateRiskScore(customer, model, previousScore);

    // Determine alert type
    const alertTypes = ['Dư nợ quá hạn', 'Thanh toán trễ hạn', 'Biến động bất thường', 'Điểm tín dụng thấp', 'Rủi ro thanh khoản'];
    const alertType = alertTypes[Math.floor(Math.random() * alertTypes.length)];

    // Generate recommendations based on risk level
    let recommendations = [];
    if (riskLevel === 'high') {
      recommendations = [
        'Liên hệ khách hàng ngay để xác minh tình hình tài chính',
        'Yêu cầu bổ sung tài liệu chứng minh thu nhập và dòng tiền',
        'Xem xét điều chỉnh hạn mức tín dụng hoặc tạm dừng cho vay',
        'Thiết lập lịch trả nợ mới với điều khoản chặt chẽ hơn',
        'Tăng tần suất theo dõi lên hàng tuần'
      ];
    } else if (riskLevel === 'medium') {
      recommendations = [
        'Theo dõi tiếp diễn biến trong 2-4 tuần tới',
        'Gửi thông báo nhắc nhở về lịch thanh toán sắp tới',
        'Xem xét cung cấp hỗ trợ tư vấn tài chính nếu cần',
        'Đánh giá lại định kỳ sau 1 tháng'
      ];
    } else {
      recommendations = [
        'Tiếp tục theo dõi định kỳ theo quy định',
        'Ghi nhận xu hướng tích cực',
        'Xem xét ưu đãi cho khách hàng tốt'
      ];
    }

    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');

    if (existingWarning) {
      const existingHistory = Array.isArray(existingWarning.history) ? existingWarning.history : [];
      // Update existing warning
      const updatedWarning = {
        ...existingWarning,
        modelId: model.id,
        modelName: model.name,
        score,
        riskLevel,
        indicators: indicatorScores,
        lastCheckedDate: now,
        lastCheckedBy: username,
        history: [
          {
            id: existingHistory.length + 1,
            date: now,
            score,
            riskLevel,
            status: existingWarning.status,
            action: 'Chạy lại đánh giá',
            user: username,
            notes: `Cập nhật điểm từ ${existingWarning.score} thành ${score}`
          },
          ...existingHistory
        ]
      };
      updateWarning(existingWarning.id, updatedWarning);
      return updatedWarning;
    } else {
      // Create new warning
      const newWarning = {
        id: `ALT-${String(warnings.length + 1).padStart(3, '0')}`,
        code: `ALT-${String(warnings.length + 1).padStart(3, '0')}`,
        customerId: customer.id,
        customerName: customer.name,
        customerType: customer.type,
        modelId: model.id,
        modelName: model.name,
        score,
        maxScore: 100,
        riskLevel,
        status: 'new',
        alertType,
        createdDate: now,
        createdBy: username,
        lastCheckedDate: now,
        lastCheckedBy: username,
        assignedTo: null,
        resolvedDate: null,
        resolvedBy: null,
        indicators: indicatorScores,
        history: [
          {
            id: 1,
            date: now,
            score,
            riskLevel,
            status: 'new',
            action: 'Tạo cảnh báo tự động',
            user: username,
            notes: `Phát hiện mức độ rủi ro ${riskLevel === 'high' ? 'cao' : riskLevel === 'medium' ? 'trung bình' : 'thấp'} từ mô hình ${model.name}`
          }
        ],
        recommendations,
        notes: null
      };
      addWarning(newWarning);
      return newWarning;
    }
  };

  // Run warning check for all customers
  const runWarningForAll = (modelId, username = 'System Auto') => {
    const model = models.find(m => m.id === modelId);
    if (!model) {
      return [];
    }

    const results = customers.map(customer => {
      return runWarningForCustomer(customer.id, modelId, username);
    }).filter(Boolean);

    return results;
  };

  // ===== CASES MANAGEMENT (CBS & CAP Workflow) =====
  const [cases, setCases] = useState(() => {
    try {
      const savedCases = localStorage.getItem('ews_cases');
      return savedCases ? JSON.parse(savedCases) : [];
    } catch (error) {
      console.error('Failed to load cases from localStorage:', error);
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('ews_cases', JSON.stringify(cases));
    } catch (error) {
      console.error('Failed to persist cases to localStorage:', error);
    }
  }, [cases]);

  // If all cases are cleared, move orphan in-progress warnings back to new.
  useEffect(() => {
    if (cases.length !== 0) {
      return;
    }

    setWarnings(prevWarnings => {
      let changed = false;
      const syncedWarnings = prevWarnings.map(warning => {
        if (warning.status !== 'in_progress') {
          return warning;
        }

        changed = true;
        return {
          ...warning,
          status: 'new',
          assignedTo: null,
          resolvedDate: null,
          resolvedBy: null
        };
      });

      return changed ? syncedWarnings : prevWarnings;
    });
  }, [cases]);

  const addCase = (caseData) => {
    setCases([...cases, caseData]);
  };

  const updateCase = (updatedCase) => {
    setCases(cases.map(c => c.id === updatedCase.id ? updatedCase : c));
  };

  const deleteCase = (caseId) => {
    setCases(cases.filter(c => c.id !== caseId));
  };

  const getCaseById = (caseId) => {
    return cases.find(c => c.id === caseId);
  };

  const getCasesByCustomer = (customerId) => {
    return cases.filter(c => c.customerId === customerId);
  };

  // ===== CASE FILTER RULES =====
  const [caseFilterRules, setCaseFilterRules] = useState([]);

  const addCaseFilterRule = (rule) => {
    const newRules = [...caseFilterRules, rule];
    setCaseFilterRules(newRules);
    
    // Nếu rule được tạo và đang active, chạy lại filter cho tất cả warnings
    if (rule.isActive) {
      // Gọi trực tiếp với rules mới
      setTimeout(() => {
        console.log('🔄 Trigger reapply từ addCaseFilterRule:', rule.name);
        reapplyAllFilterRulesWithRules(newRules);
      }, 200);
    }
  };

  const updateCaseFilterRule = (updatedRule) => {
    const newRules = caseFilterRules.map(r => r.id === updatedRule.id ? updatedRule : r);
    setCaseFilterRules(newRules);
    
    // Nếu rule được kích hoạt (từ inactive -> active), chạy lại filter
    const oldRule = caseFilterRules.find(r => r.id === updatedRule.id);
    if (updatedRule.isActive && (!oldRule || !oldRule.isActive)) {
      setTimeout(() => {
        console.log('🔄 Trigger reapply từ updateCaseFilterRule:', updatedRule.name);
        reapplyAllFilterRulesWithRules(newRules);
      }, 200);
    }
  };

  const deleteCaseFilterRule = (ruleId) => {
    setCaseFilterRules(caseFilterRules.filter(r => r.id !== ruleId));
  };

  const clearSampleData = () => {
    const resetWarnings = extendedWarnings.map(warning => {
      const normalizedWarning = normalizeInitWarning(warning, extendedModels, indicators);
      const resetDate = normalizedWarning.createdDate || new Date().toISOString().slice(0, 16).replace('T', ' ');

      return {
        ...normalizedWarning,
        status: 'new',
        assignedTo: null,
        resolvedDate: null,
        resolvedBy: null,
        lastCheckedDate: resetDate,
        lastCheckedBy: 'System Init',
        history: [
          {
            id: 1,
            date: resetDate,
            score: normalizedWarning.score,
            riskLevel: normalizedWarning.riskLevel || 'low',
            status: 'new',
            action: 'Khởi tạo dữ liệu cảnh báo',
            user: 'System Init',
            notes: 'Reset dữ liệu mẫu về trạng thái ban đầu.'
          }
        ]
      };
    });

    setModels(extendedModels);
    setCustomers(extendedCustomers);
    setWarnings(resetWarnings);
    setCases([]);
    setCaseFilterRules([]);

    // Reset persisted sample data so app starts fresh on next reload.
    localStorage.removeItem('ews_cases');
    localStorage.removeItem('ews_users');
  };

  const initSampleData = () => {
    clearSampleData();
  };

  const clearChangedData = () => {
    clearSampleData();
  };
  
  // Chạy lại filter rules cho TẤT CẢ warnings hiện có (từ button)
  const reapplyAllFilterRules = () => {
    reapplyAllFilterRulesWithRules(caseFilterRules);
  };
  
  // Internal function với rules parameter
  const reapplyAllFilterRulesWithRules = (rulesToApply) => {
    console.log('🔄 Đang chạy lại filter rules cho tất cả warnings...');
    console.log('📋 Total rules:', rulesToApply.length);
    
    const activeRules = rulesToApply.filter(r => r.isActive);
    console.log('✅ Active rules:', activeRules.length, activeRules.map(r => r.name));
    
    if (activeRules.length === 0) {
      console.log('⚠️ Không có rule nào đang active');
      return;
    }
    
    console.log('📊 Total warnings:', warnings.length);
    console.log('📦 Total cases:', cases.length);
    
    // Filter warnings that don't have cases yet AND whose customer doesn't have active case
    const warningsWithoutCase = warnings.filter(w => 
      w.status !== 'resolved' && 
      !cases.some(c => c.warningId === w.id)
    );
    
    // Get unique customers from these warnings who DON'T have active cases
    const customersWithoutCase = new Set();
    const eligibleWarnings = warningsWithoutCase.filter(w => {
      const hasActiveCase = cases.some(c => c.customerId === w.customerId && c.status !== 'closed');
      if (!hasActiveCase && !customersWithoutCase.has(w.customerId)) {
        customersWithoutCase.add(w.customerId);
        return true; // Take first warning for this customer
      }
      return false;
    });
    
    console.log('🔍 Warnings chưa có case:', warningsWithoutCase.length);
    console.log('👥 Customers chưa có active case:', customersWithoutCase.size);
    console.log('📝 Warnings đủ điều kiện tạo case:', eligibleWarnings.length);
    
    let casesCreated = 0;
    
    // Check từng warning eligible
    eligibleWarnings.forEach((warning, idx) => {
      // Get customer info
      const customer = customers.find(c => c.id === warning.customerId);
      if (!customer) {
        console.log(`⚠️ Warning ${warning.code}: Không tìm thấy customer ${warning.customerId}`);
        return;
      }
      
      if (idx < 3) { // Debug first 3 warnings
        console.log(`\n🔍 Checking warning ${warning.code}:`, {
          riskLevel: warning.riskLevel,
          customerType: warning.customerType || customer.type,
          customerGroupId: customer.groupId
        });
      }
      
      // Check từng rule
      for (const rule of activeRules) {
        let matchCount = 0;
        let totalConditions = 0;
        let debugInfo = { rule: rule.name, matches: [] };
        
        // Check risk level
        if (rule.riskLevels && rule.riskLevels.length > 0) {
          totalConditions++;
          const riskMatch = rule.riskLevels.includes(warning.riskLevel);
          if (riskMatch) matchCount++;
          
          if (idx < 3) {
            debugInfo.matches.push(`Risk: ${warning.riskLevel} in [${rule.riskLevels}] = ${riskMatch}`);
          }
        }
        
        // Check customer type
        if (rule.customerTypes && rule.customerTypes.length > 0) {
          totalConditions++;
          let custType = warning.customerType || customer?.type || '';
          
          // Normalize customer type - strip "Khách hàng" prefix if exists
          const normalizedCustType = custType.replace(/^Khách hàng\s+/i, '').trim();
          
          // Check if normalized type matches any rule types
          const typeMatch = rule.customerTypes.some(ruleType => {
            const normalizedRuleType = ruleType.replace(/^Khách hàng\s+/i, '').trim();
            return normalizedCustType.toLowerCase() === normalizedRuleType.toLowerCase();
          });
          
          if (typeMatch) matchCount++;
          
          if (idx < 3) {
            debugInfo.matches.push(`Type: "${custType}" (normalized: "${normalizedCustType}") in [${rule.customerTypes}] = ${typeMatch}`);
          }
        }
        
        // Check customer group - only if customer has groupId
        if (rule.customerGroups && rule.customerGroups.length > 0) {
          if (customer.groupId) {
            // Customer has group, check if it matches
            totalConditions++;
            const groupMatch = rule.customerGroups.includes(customer.groupId);
            if (groupMatch) matchCount++;
            
            if (idx < 3) {
              debugInfo.matches.push(`Group: ${customer.groupId} in [${rule.customerGroups}] = ${groupMatch}`);
            }
          } else {
            // Customer has no group - skip this condition (don't count in totalConditions)
            if (idx < 3) {
              debugInfo.matches.push(`Group: undefined (skipped - customer has no group)`);
            }
          }
        }
        
        // Determine if rule matches
        let matched = false;
        if (rule.matchCondition === 'any') {
          matched = matchCount > 0;
        } else {
          matched = totalConditions > 0 && matchCount === totalConditions;
        }
        
        if (idx < 3) {
          console.log(`  ${debugInfo.rule}: ${debugInfo.matches.join(', ')} => ${rule.matchCondition} => ${matched ? '✅ MATCH' : '❌ NO MATCH'} (${matchCount}/${totalConditions})`);
        }
        
        // If matched, create case automatically (customer is guaranteed to not have active case)
        if (matched) {
          const autoCase = {
            id: `CASE-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            warningId: warning.id,
            warningCode: warning.code,
            warningLevel: warning.riskLevel,
            warningScore: warning.score,
            alertType: warning.alertType,
            customerId: customer.id,
            customerName: customer.name,
            customerType: warning.customerType || customer.type,
            riskLevel: warning.riskLevel,
            currentWorkflow: 'cbs',
            currentStepId: 'cbs_4',
            status: 'in_progress',
            isManual: false,
            isHidden: false,
            ruleId: rule.id,
            ruleName: rule.name,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            history: [
              {
                stepId: 'cbs_1',
                action: 'auto_created',
                timestamp: new Date().toISOString(),
                user: 'System',
                notes: `Case tự động tạo từ rule: ${rule.name}`,
                status: 'completed'
              }
            ],
            data: {
              warningData: {
                score: warning.score,
                maxScore: warning.maxScore,
                thresholds: warning.thresholds,
                details: warning.details,
                model: warning.modelName
              }
            }
          };
          
          setCases(prev => {
            const newCases = [...prev, autoCase];
            console.log(`✅ Tạo case ${autoCase.id} từ rule "${rule.name}" cho warning ${warning.code}`);
            console.log(`   Customer: ${autoCase.customerId} - ${autoCase.customerName}`);
            console.log(`   Total cases sau khi thêm: ${newCases.length}`);
            return newCases;
          });
          casesCreated++;
          
          // Update warning status
          setWarnings(prev => prev.map(w => 
            w.id === warning.id ? { ...w, status: 'in_progress' } : w
          ));
          
          // Chỉ tạo 1 case per warning (first matching rule)
          break;
        }
      }
    });
    
    console.log(`✅ Hoàn thành! Đã tạo ${casesCreated} cases từ filter rules`);
  };

  // Check if a warning matches filter rules
  const checkWarningMatchesRules = (warning, customer) => {
    const activeRules = caseFilterRules.filter(r => r.isActive);
    
    if (activeRules.length === 0) return false; // No active rules = no auto-filter
    
    // Check each rule
    for (const rule of activeRules) {
      let matchCount = 0;
      let totalConditions = 0;
      
      // Check risk level
      if (rule.riskLevels && rule.riskLevels.length > 0) {
        totalConditions++;
        if (rule.riskLevels.includes(warning.riskLevel)) {
          matchCount++;
        }
      }
      
      // Check customer type
      if (rule.customerTypes && rule.customerTypes.length > 0) {
        totalConditions++;
        if (rule.customerTypes.includes(warning.customerType || customer?.type)) {
          matchCount++;
        }
      }
      
      // Check customer group
      if (rule.customerGroups && rule.customerGroups.length > 0 && customer) {
        totalConditions++;
        if (rule.customerGroups.includes(customer.groupId)) {
          matchCount++;
        }
      }
      
      // Determine if rule matches based on match condition
      if (rule.matchCondition === 'any') {
        // ANY: At least one condition must match
        if (matchCount > 0) return true;
      } else {
        // ALL: All conditions must match
        if (totalConditions > 0 && matchCount === totalConditions) return true;
      }
    }
    
    return false;
  };

  const value = {
    // Source Definitions
    sourceDefinitions,
    setSourceDefinitions,
    
    // Data Sources
    dataSources,
    addDataSource,
    updateDataSource,
    deleteDataSource,
    
    // Indicators
    indicators,
    addIndicator,
    updateIndicator,
    deleteIndicator,
    toggleIndicatorStatus,
    getIndicatorById,
    getActiveIndicators,
    
    // Models
    models,
    addModel,
    updateModel,
    deleteModel,
    toggleModelStatus,

    // Customers
    customers,
    addCustomer,
    updateCustomer,
    deleteCustomer,
    getCustomerById,

    // Customer Groups
    customerGroups,
    addCustomerGroup,
    updateCustomerGroup,
    deleteCustomerGroup,
    getCustomerGroupById,

    // Warnings
    warnings,
    addWarning,
    updateWarning,
    deleteWarning,
    getWarningById,
    addWarningHistory,
    runWarningForCustomer,
    runWarningForAll,

    // Cases (CBS & CAP Workflow)
    cases,
    addCase,
    updateCase,
    deleteCase,
    getCaseById,
    getCasesByCustomer,
    
    // Case Filter Rules
    caseFilterRules,
    addCaseFilterRule,
    updateCaseFilterRule,
    deleteCaseFilterRule,
    checkWarningMatchesRules,
    applyAutoFilterToWarning,
    reapplyAllFilterRules,
    clearSampleData,
    initSampleData,
    clearChangedData
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

